import { useState } from "react";
import "./ChartTabs.css";

import ChargesPerDayChart from "./ChargesPerDayChart";
import AverageChargesDayOfWeekChart from "./AverageChargesChart";
import EnergyBoxplotChart from "./EnergyBoxplotChart";
import AverageEnergyChart from "./AverageEnergyChart";
import TimeBoxplotChart from "./TimeBoxplotChart";
import AverageTimeChart from "./AverageTimeChart";
import TotalEnergyChart from "./TotalEnergyChart";
import AverageEnergyByHourChart from "./AverageEnergyByHourChart";
import UtilizationByHourChart from "./UtilizationByHourChart";
import StationEnergyChart from "./StationEnergyChart";
import StationTimeChart from "./StationTimeChart";
import StationTotalEnergyChart from "./StationTotalEnergyChart";


function ChartTabs({
  chargerType,
  grouping,

  acData,
  dcData,

  acDayOfWeekData,
  dcDayOfWeekData,

  energyBoxplotData,
  averageEnergyData,

  timeBoxplotData,
  averageTimeData,

  acTotalEnergyData,
  dcTotalEnergyData,

  averageEnergyByHourData,
  utilizationByHourData,

  stationSummaryData = []
}) {

  // Stores which chart topic is currently selected.
  const [activeTab, setActiveTab] = useState("charges");


  // Defines the available chart tabs and their display labels.
  const tabs = [
    {
      id: "charges",
      label: "Number of charges"
    },
    {
      id: "weekday",
      label: "Average number of charges"
    },
    {
      id: "energy",
      label: "Energy per charge"
    },
    {
      id: "time",
      label: "Charging time"
    },
    {
      id: "total-energy",
      label: "Energy consumption"
    },
    {
      id: "time-of-day",
      label: "Time of day"
    }
  ];


  // Determine whether AC and/or DC charts should be displayed.
  const showAC = chargerType === "Both" || chargerType === "AC";
  const showDC = chargerType === "Both" || chargerType === "DC";


  return (
    <section className="charts-panel">
      <div className="charts-panel-header">
        <div>
          <span className="charts-eyebrow">
            Charging insights
          </span>

          <h2>Dashboard visualisations</h2>

          <p>
            Select a topic to explore the filtered charging data.
          </p>
        </div>

        {/* Display the currently selected charger filter */}
        <span className="charts-filter-status">
          {chargerType === "Both"
            ? "AC and DC"
            : `${chargerType} chargers`}
        </span>
      </div>


      {/* Create a button for each chart topic */}
      <div
        className="chart-tabs"
        role="tablist"
        aria-label="Chart topics"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`chart-tab ${
              activeTab === tab.id ? "chart-tab-active" : ""
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>


      <div className="chart-tab-content">

        {/* Number of charging sessions */}
        {activeTab === "charges" && (
          <>
            <div className="chart-topic-heading">
              <div>
                <h3>Number of charging sessions</h3>

                <p>
                  Charging sessions over time for the selected locations and
                  date range.
                </p>
              </div>
            </div>


            <div className="chart-topic-grid">
              {showAC && (
                <ChargesPerDayChart
                  title="Number of AC Charges per Day"
                  data={acData}
                  grouping={grouping}
                />
              )}

              {showDC && (
                <ChargesPerDayChart
                  title="Number of DC Charges per Day"
                  data={dcData}
                  grouping={grouping}
                />
              )}
            </div>
          </>
        )}


        {/* Average charging activity by day of the week */}
        {activeTab === "weekday" && (
          <>
            <div className="chart-topic-heading">
              <div>
                <h3>Average charges by day of week</h3>

                <p>
                  Compare typical charging activity from Monday through Sunday.
                </p>
              </div>
            </div>


            <div className="chart-topic-grid">
              {showAC && (
                <AverageChargesDayOfWeekChart
                  title="Average AC Charges per Day of Week"
                  data={acDayOfWeekData}
                />
              )}

              {showDC && (
                <AverageChargesDayOfWeekChart
                  title="Average DC Charges per Day of Week"
                  data={dcDayOfWeekData}
                />
              )}
            </div>
          </>
        )}


        {/* Energy distribution and average energy usage */}
        {activeTab === "energy" && (
          <>
            <div className="chart-topic-heading">
              <div>
                <h3>Energy per charging session</h3>

                <p>
                  Examine overall energy distribution and compare energy
                  delivered across individual charging stations.
                </p>
              </div>
            </div>


            <div className="chart-topic-grid">
              <EnergyBoxplotChart
                data={energyBoxplotData}
                chargerType={chargerType}
              />

              <AverageEnergyChart
                data={averageEnergyData}
                grouping={grouping}
                chargerType={chargerType}
              />

              <StationEnergyChart
                data={stationSummaryData}
                chargerType={chargerType}
              />
            </div>
          </>
        )}


        {/* Charging session duration */}
        {activeTab === "time" && (
          <>
            <div className="chart-topic-heading">
              <div>
                <h3>Charging duration</h3>

                <p>
                  Compare overall charging-session length and average charging
                  time across individual stations.
                </p>
              </div>
            </div>


            <div className="chart-topic-grid">
              <TimeBoxplotChart
                data={timeBoxplotData}
                chargerType={chargerType}
              />

              <AverageTimeChart
                data={averageTimeData}
                grouping={grouping}
                chargerType={chargerType}
              />

              <StationTimeChart
                data={stationSummaryData}
                chargerType={chargerType}
              />
            </div>
          </>
        )}


        {/* Total energy consumption over time */}
        {activeTab === "total-energy" && (
          <>
            <div className="chart-topic-heading">
              <div>
                <h3>Total energy consumption</h3>

                <p>
                  Review total energy consumed across the selected period and
                  compare consumption between individual stations.
                </p>
              </div>
            </div>


            <div className="chart-topic-grid">
              {showAC && (
                <TotalEnergyChart
                  title="Total AC Energy Consumption"
                  data={acTotalEnergyData}
                  grouping={grouping}
                />
              )}

              {showDC && (
                <TotalEnergyChart
                  title="Total DC Energy Consumption"
                  data={dcTotalEnergyData}
                  grouping={grouping}
                />
              )}

              <StationTotalEnergyChart
                data={stationSummaryData}
                chargerType={chargerType}
              />
            </div>
          </>
        )}


        {/* Hourly energy use and charger utilisation */}
        {activeTab === "time-of-day" && (
          <>
            <div className="chart-topic-heading">
              <div>
                <h3>Charging activity by time of day</h3>

                <p>
                  Compare energy delivery and outlet utilisation across each
                  hour of the day.
                </p>
              </div>
            </div>


            <div className="chart-topic-grid">
              <AverageEnergyByHourChart
                data={averageEnergyByHourData}
                chargerType={chargerType}
              />

              <UtilizationByHourChart
                data={utilizationByHourData}
                chargerType={chargerType}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}


export default ChartTabs;